'use client';

import React, { useState, useEffect } from 'react';
import { Share2, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUser } from '@clerk/nextjs';
import { toast } from 'sonner';

interface EventActionsProps {
	eventId: string;
}

export function EventActions({ eventId }: EventActionsProps) {
	const [isFavorited, setIsFavorited] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { isSignedIn } = useUser();

	useEffect(() => {
		// Check if event is favorited on mount
		async function checkFavoriteStatus() {
			if (!isSignedIn) return;

			try {
				console.log('Checking favorite status for event:', eventId);
				const response = await fetch('/api/users/favorites');
				console.log('Response status:', response.status);
				const data = await response.json();
				console.log('API Response data:', data);
				const isFavorited = (data.favorites || []).some(
					(fav: any) => fav.event.id === eventId
				);
				console.log('Is event favorited?', isFavorited);
				setIsFavorited(isFavorited);
			} catch (error) {
				console.error('Error checking favorite status:', error);
			}
		}

		checkFavoriteStatus();
	}, [eventId, isSignedIn]);

	const handleShare = async (e: React.MouseEvent) => {
		e.preventDefault(); // Prevent event bubbling if inside a link
		try {
			await navigator.share({
				title: document.title,
				url: window.location.href,
			});
		} catch (error) {
			// Fallback to copying to clipboard
			navigator.clipboard.writeText(window.location.href);
			toast.success('Link copied to clipboard');
		}
	};

	const handleLike = async (e: React.MouseEvent) => {
		e.preventDefault();

		if (!isSignedIn) {
			toast.error('Please sign in to favorite events');
			return;
		}

		setIsLoading(true);
		try {
			if (isFavorited) {
				console.log('Removing from favorites:', eventId);
				const response = await fetch(
					`/api/users/favorites?eventId=${eventId}`,
					{
						method: 'DELETE',
					}
				);
				console.log('Delete response status:', response.status);

				if (!response.ok) {
					const errorData = await response.json();
					console.error('Delete error data:', errorData);
					throw new Error(errorData.error || 'Failed to remove from favorites');
				}

				setIsFavorited(false);
				toast.success('Removed from favorites');
			} else {
				console.log('Adding to favorites:', eventId);
				const response = await fetch('/api/users/favorites', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ eventId }),
				});
				console.log('Add response status:', response.status);

				if (!response.ok) {
					const errorData = await response.json();
					console.error('Add error data:', errorData);
					throw new Error(errorData.error || 'Failed to add to favorites');
				}

				setIsFavorited(true);
				toast.success('Added to favorites');
			}
		} catch (error) {
			console.error('Error toggling favorite:', error);
			toast.error(
				error instanceof Error ? error.message : 'Failed to update favorites'
			);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className='flex gap-2'>
			<Button
				variant='ghost'
				size='icon'
				className='h-9 w-9 rounded-full bg-background/50 hover:bg-background/80'
				onClick={handleShare}
			>
				<Share2 className='w-5 h-5' />
			</Button>
			<Button
				variant='ghost'
				size='icon'
				className='h-9 w-9 rounded-full bg-background/50 hover:bg-background/80'
				disabled={isLoading}
				onClick={handleLike}
			>
				<Heart
					className={`h-5 w-5 ${
						isFavorited
							? 'fill-foreground text-foreground'
							: 'text-foreground/50'
					} ${isLoading ? 'animate-pulse' : ''}`}
				/>
			</Button>
		</div>
	);
}
